/**
 * An idle console signs itself out gracefully, as the customer portal does: after 25 minutes with
 * no mouse, keyboard, touch or scroll, "Are you still there?" counts down two minutes, then signs
 * the person out. Activity while the warning shows does not dismiss it by accident; only "I'm still
 * here" does. And while the tab is visible, its token is renewed every ten minutes, so a long-open
 * console does not discover an expired sign-in on its next click. Mounted inside the signed-in
 * shell only; off with a development token (signing out would only reload it).
 */

import { AlertDialog, Button } from '@heroui/react';
import { useEffect, useRef, useState } from 'react';

import { currentToken } from '../api/client.ts';
import { useAuth } from '../auth/AuthProvider.tsx';

const IDLE_TIMEOUT_MS = 25 * 60 * 1000;
const WARNING_DURATION_MS = 2 * 60 * 1000;
const REFRESH_INTERVAL_MS = 10 * 60 * 1000;
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll', 'wheel'] as const;
const DEV_TOKEN = import.meta.env.DEV && Boolean(import.meta.env.VITE_DEV_TOKEN);

const countdown = (ms: number) => {
  const s = Math.max(0, Math.ceil(ms / 1000));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};

export function IdleSessionGuard() {
  const { signOut } = useAuth();
  const signOutRef = useRef(signOut);
  useEffect(() => {
    signOutRef.current = signOut;
  });
  /** When the person last did something; set when the guard starts watching. */
  const lastActivity = useRef(0);
  /** Set while the warning shows; activity then is ignored. */
  const warningSince = useRef<number | null>(null);
  const [warning, setWarning] = useState(false);
  const [remaining, setRemaining] = useState(WARNING_DURATION_MS);

  useEffect(() => {
    if (DEV_TOKEN) return;
    lastActivity.current = Date.now();
    const seen = () => {
      if (warningSince.current === null) lastActivity.current = Date.now();
    };
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, seen, { passive: true }));
    const tick = setInterval(() => {
      const now = Date.now();
      if (warningSince.current === null) {
        if (now - lastActivity.current >= IDLE_TIMEOUT_MS) {
          warningSince.current = now;
          setRemaining(WARNING_DURATION_MS);
          setWarning(true);
        }
        return;
      }
      const left = WARNING_DURATION_MS - (now - warningSince.current);
      if (left <= 0) {
        warningSince.current = null;
        setWarning(false);
        signOutRef.current({ idle: true });
      } else setRemaining(left);
    }, 1000);
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, seen));
      clearInterval(tick);
    };
  }, []);

  // Keep a visible tab's token warm; a hidden or sleeping one renews on its next real request.
  useEffect(() => {
    if (DEV_TOKEN) return;
    let id: ReturnType<typeof setInterval> | undefined;
    const refresh = () => void currentToken().catch(() => undefined);
    const startOrStop = () => {
      if (document.visibilityState === 'visible') {
        if (!id) id = setInterval(refresh, REFRESH_INTERVAL_MS);
      } else if (id) {
        clearInterval(id);
        id = undefined;
      }
    };
    startOrStop();
    document.addEventListener('visibilitychange', startOrStop);
    return () => {
      document.removeEventListener('visibilitychange', startOrStop);
      if (id) clearInterval(id);
    };
  }, []);

  const stay = () => {
    lastActivity.current = Date.now();
    warningSince.current = null;
    setWarning(false);
  };

  if (!warning) return null;
  return (
    <AlertDialog.Backdrop isOpen isDismissable={false}>
      <AlertDialog.Container>
        <AlertDialog.Dialog className="sm:max-w-md">
          <AlertDialog.Header>
            <AlertDialog.Icon status="warning" />
            <AlertDialog.Heading>Are you still there?</AlertDialog.Heading>
          </AlertDialog.Header>
          <AlertDialog.Body>
            <p className="text-sm text-muted">You’ll be signed out in {countdown(remaining)} because the console has been idle.</p>
          </AlertDialog.Body>
          <AlertDialog.Footer>
            <Button variant="tertiary" onPress={() => signOutRef.current()}>
              Sign out now
            </Button>
            <Button variant="primary" autoFocus onPress={stay}>
              I’m still here
            </Button>
          </AlertDialog.Footer>
        </AlertDialog.Dialog>
      </AlertDialog.Container>
    </AlertDialog.Backdrop>
  );
}
